import { z } from 'zod';
import { noyauSchema } from './noyau.js';
import { palierDefaultSchema, resolvedPalierSchema } from './palier.js';
import { singularityLevierSchema } from './singularity-setup.js';

/** The three image formats a stored logo can have (issue #20), as media types. */
export const LOGO_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'] as const;
export type LogoMimeType = (typeof LOGO_MIME_TYPES)[number];

/** A logo is refused above this size, in the browser and again on the server. */
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;

/** Zoom bounds of a logo's framing: 1 is the whole logo in the circle. */
export const LOGO_ZOOM_MIN = 1;
export const LOGO_ZOOM_MAX = 4;

/**
 * At zoom 1 the logo's larger side spans this share of the circle, so the whole
 * logo sits inside it with a margin. Every circle of the app draws the image
 * from this one number, which is what keeps the framing identical at 64, 80 and
 * 240 px.
 */
export const LOGO_FIT_RATIO = 11 / 12;

/**
 * How a logo sits in its circle (issue #20): the zoom, and where the image's
 * centre lies relative to the circle's centre, in fractions of the square box
 * the image is fitted in at that zoom (for a landscape logo, a fraction of its
 * width). Only this framing is stored, never a cropped image, so recropping
 * later loses nothing. The offsets are bounded to ±0.5; the editor narrows
 * them further so that the circle stays covered whenever the zoom allows it.
 */
export const logoFramingSchema = z.object({
  zoom: z.number().min(LOGO_ZOOM_MIN).max(LOGO_ZOOM_MAX),
  offsetX: z.number().min(-0.5).max(0.5),
  offsetY: z.number().min(-0.5).max(0.5),
});
export type LogoFraming = z.infer<typeof logoFramingSchema>;

/** The framing of a logo that was just chosen: whole, centred. */
export const NEUTRAL_LOGO_FRAMING: LogoFraming = { zoom: 1, offsetX: 0, offsetY: 0 };

/**
 * What the frontend draws: `src` is the stored logo (a path under the API,
 * versioned for the cache) or, for a company that only has its old URL, that
 * URL; the framing applies to both.
 */
export const companyLogoSchema = logoFramingSchema.extend({
  src: z.string().min(1),
});
export type CompanyLogo = z.infer<typeof companyLogoSchema>;

/** Why the logo routes refuse an image; the frontend maps each code to its copy. */
export const LOGO_REJECTION_CODES = [
  'LOGO_FORMAT_UNSUPPORTED',
  'LOGO_TOO_LARGE',
  'LOGO_URL_UNREACHABLE',
  'LOGO_SVG_UNSAFE',
  'LOGO_SVG_NO_SIZE',
] as const;
export type LogoRejectionCode = (typeof LOGO_REJECTION_CODES)[number];

export const companySchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(200),
  /**
   * The company's old, hand-typed logo URL. It stays as a fallback for the
   * companies that never got a stored logo; saving or removing a logo clears
   * it. The frontend draws `logo` and never reads this directly.
   */
  logoUrl: z.string().url().nullable(),
  /** The logo to draw, with its framing; null when the company has none. */
  logo: companyLogoSchema.nullable(),
  noyau: noyauSchema,
  /**
   * Company-level context — read-only in the wizard, surfaces only in the
   * récapitulatif's composed prompt. Distinct from `matrices.contexte`,
   * which is edited per-matrix and never pre-filled from this field.
   */
  contexte: z.string().max(10_000).nullable(),
  /** One company-wide free-text Braavo-niveau equivalence — no longer per-palier. */
  braavoLevel: z.string().max(200).nullable(),
  /**
   * When the company was archived (issue #20), null while it is active. An
   * archived company is shelved and closed: out of the grid, its pages show a
   * notice, and the API refuses every write on it and on its matrices.
   */
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Company = z.infer<typeof companySchema>;

/**
 * One row of `GET /companies?archived=true` (issue #20): an archived company
 * with the number of matrices a permanent deletion would take with it, which
 * the confirmation dialog states.
 */
export const archivedCompanySchema = companySchema.extend({
  archivedAt: z.string().datetime(),
  matrixCount: z.number().int().min(0),
});
export type ArchivedCompany = z.infer<typeof archivedCompanySchema>;

/**
 * What the wizard's setup-review step actually fetches: the company plus
 * its resolved singularity setup and paliers, with completeness already
 * computed server-side (the route-level guard checks this field).
 */
export const companyWithSetupSchema = companySchema.extend({
  singularitySetup: z.array(singularityLevierSchema).length(6),
  paliers: z.array(resolvedPalierSchema).length(8),
  /**
   * The 8 global defaults, alongside the resolved view (issue #1).
   *
   * `paliers` above already merges them in, so this looks redundant — it is
   * not. The paliers editor has to offer "reset to default", and a resolved
   * value cannot tell you what it would fall back to. Sending the level below
   * is what lets the editor mark which paliers are actually overridden and
   * restore the inherited value without a second round trip.
   */
  palierDefaults: z.array(palierDefaultSchema).length(8),
  isSetupComplete: z.boolean(),
});
export type CompanyWithSetup = z.infer<typeof companyWithSetupSchema>;

/**
 * `POST /companies` — noyau is picked at creation (not deferred like the rest
 * of the singularity setup). The logo is no longer sent here (issue #20): the
 * frontend uploads it with `PUT /companies/:id/logo` right after the company
 * exists. `logoUrl` stays accepted for the old API consumers.
 */
export const createCompanyInputSchema = z.object({
  name: z.string().min(1).max(200),
  noyau: noyauSchema,
  logoUrl: z.string().url().optional(),
});
export type CreateCompanyInput = z.infer<typeof createCompanyInputSchema>;

/**
 * `PATCH /companies/:id` — "Paramètres entreprise" tab. `noyau` is editable
 * here too ("in case of error" — see docs/design/.../2026-08-06 grill-me
 * decision 3), same as name/logoUrl/contexte.
 */
export const patchCompanyInputSchema = companySchema
  .pick({ name: true, noyau: true, logoUrl: true, contexte: true, braavoLevel: true })
  .partial();
export type PatchCompanyInput = z.infer<typeof patchCompanyInputSchema>;

/** `PUT /companies/:id/logo/from-url` — the server downloads the image once and stores it. */
export const logoFromUrlInputSchema = z.object({ url: z.string().url().max(2048) });
export type LogoFromUrlInput = z.infer<typeof logoFromUrlInputSchema>;

/** `PATCH /companies/:id/logo` — saves the framing of the logo the company already has. */
export const patchLogoFramingInputSchema = logoFramingSchema;
export type PatchLogoFramingInput = z.infer<typeof patchLogoFramingInputSchema>;
